import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import { useOnboardingContext } from "@/modules/onboarding/context";
import { GenderPickerScreen } from "@/modules/onboarding/screens/GenderPickerScreen";
import { storage } from "@/modules/storage";
import {
  getMultiPlatformPlatformByPlatformIdLocations,
  getPlatform,
  getReferralCodesByReferralCode,
} from "api/methods";

const V3GenderPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    platformSelection,
    profile,
    updateProfileState,
    auth,
    updateProfileAPI,
    referral,
    updatePlatformSelection,
  } = useOnboardingContext();
  const initialGender = profile.gender ?? "";

  const { data: platform } = useQuery({ ...getPlatform.keyed() });

  const { data: locations = [] } = useQuery({
    ...getMultiPlatformPlatformByPlatformIdLocations.keyed({
      platformId: platformSelection.platformId ?? "",
    }),
    select: ({ locations }) => locations ?? [],
    enabled: !!platformSelection.platformId,
  });

  const { data: referralCodeData } = useQuery({
    ...getReferralCodesByReferralCode.keyed({
      referralCode: referral.referralCode ?? "",
    }),
    enabled: !!referral.referralCode,
    staleTime: 0,
  });

  const handleGenderSelect = useCallback(
    async (genderId: string) => {
      const isExistingUser = auth.existingUser?.completedProfile;
      const hasPersonalisationEnabled = platform?.platformSettings?.patientPersonalisationEnabled;

      if (isExistingUser && hasPersonalisationEnabled) {
        await updateProfileAPI({ gender: genderId });
        navigate("/personalise");
        return;
      }

      updateProfileState({ gender: genderId });

      const isGiftCardClaim = storage.getItem("isGiftCardClaim") === "true";
      const referralLocationId = referralCodeData?.platformLocation?.id?.value;
      const isOnlyOneLocation = locations.length === 1;

      // Validate referral location exists in available locations
      const isReferralLocationValid =
        referralLocationId && locations.some(({ id }) => id === referralLocationId);

      // Skip location selection for gift card claims only when the gift-card
      // flow already supplied a locationId (via the ?locationId query param
      // from GiftCardClaimContainer) AND that location still exists in the
      // current platform's locations. DisplayGiftCardTransferAnonymous.locationId
      // is optional, and persisted onboarding state can carry a stale id from a
      // different platform. When either is missing/invalid, fall through to the
      // normal location logic instead of jumping straight to /signin/source
      // (which would later throw "A platform location must be selected before
      // creating a user." in onOnboardingCompleted, or create the user against
      // the wrong location).
      const isGiftCardLocationValid =
        !!platformSelection.locationId &&
        locations.some(({ id }) => id === platformSelection.locationId);

      if (isGiftCardClaim && isGiftCardLocationValid) {
        navigate("/signin/source");
        return;
      }

      if (isOnlyOneLocation) {
        // Single location - use the only available location
        updatePlatformSelection({ locationId: locations[0].id });
        navigate("/signin/source");
      } else if (isReferralLocationValid) {
        // Multiple locations with valid referral - pre-select and show picker
        updatePlatformSelection({ locationId: referralLocationId });
        navigate("/signin/location");
      } else {
        // Multiple locations without valid referral - show picker
        navigate("/signin/location");
      }
    },
    [
      navigate,
      updateProfileState,
      auth.existingUser?.completedProfile,
      platform?.platformSettings?.patientPersonalisationEnabled,
      updateProfileAPI,
      locations,
      referralCodeData,
      updatePlatformSelection,
      platformSelection.locationId,
    ],
  );

  return (
    <MobileViewportContainer header={<OnboardingBackButton />}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
        className="flex w-full max-w-sm flex-col items-center"
      >
        <GenderPickerScreen
          key={`gender-${initialGender}`}
          onGenderSelect={handleGenderSelect}
          selectedGender={initialGender}
          submitButtonText={t("common.confirm")}
        />
      </motion.div>
    </MobileViewportContainer>
  );
};

export default V3GenderPage;
